import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { api } from '../api/client';
import {
  ChangeView,
  HeatmapLayer,
  getMarkerIcon,
  getRiskLabel,
  getRiskBadgeClass,
  userLocationIcon,
  calculateDistanceMiles,
  formatPopupAddress,
} from '../components/MapHelpers';

const DEFAULT_CENTER = [39.8283, -98.5795];
const DEFAULT_ZOOM = 4;
const LOCAL_ZOOM = 11; // ~10 mile radius view

function HomePage() {
  // --- Core state ---
  const [userCoords, setUserCoords] = useState(null);       // { lat, lon } or null
  const [geoStatus, setGeoStatus] = useState('loading');     // loading | granted | denied | error
  const [userZipCode, setUserZipCode] = useState(null);
  const [offenders, setOffenders] = useState([]);
  const [loadingOffenders, setLoadingOffenders] = useState(false);
  const [error, setError] = useState(null);

  // --- Controls ---
  const [searchRadius, setSearchRadius] = useState(5);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [hideIncarcerated, setHideIncarcerated] = useState(false);
  const [satelliteView, setSatelliteView] = useState(false);
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'gallery'
  const [copied, setCopied] = useState(false);

  // --- Refs for dedup ---
  const geoFetched = useRef(false);
  const geocoded = useRef(false);
  const lastFetchKey = useRef(null);

  // ==========================================
  // 1. Get user's geolocation (runs once)
  // ==========================================
  useEffect(() => {
    if (geoFetched.current) return;
    geoFetched.current = true;

    if (!navigator.geolocation) {
      setGeoStatus('denied');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setGeoStatus('granted');
      },
      (err) => {
        setGeoStatus(err.code === 1 ? 'denied' : 'error');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  // ==========================================
  // 2. Reverse geocode for display zip (runs once when coords arrive)
  // ==========================================
  useEffect(() => {
    if (!userCoords || geocoded.current) return;
    geocoded.current = true;

    fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${userCoords.lat}&longitude=${userCoords.lon}&localityLanguage=en`)
      .then(r => r.json())
      .then(data => {
        const zip = data?.postcode || data?.postalCode;
        if (zip) setUserZipCode(zip.split('-')[0].trim());
      })
      .catch(() => {}); // non-critical, just for display
  }, [userCoords]);

  // ==========================================
  // 3. Fetch offenders when coords/radius/filter change
  // ==========================================
  useEffect(() => {
    if (!userCoords) return;

    const fetchKey = `${userCoords.lat},${userCoords.lon},${searchRadius},${hideIncarcerated}`;
    if (lastFetchKey.current === fetchKey) return;
    lastFetchKey.current = fetchKey;

    const controller = new AbortController();
    setLoadingOffenders(true);
    setError(null);

    api.offenders.nearby(
      userCoords.lat,
      userCoords.lon,
      searchRadius,
      { exclude_incarcerated: hideIncarcerated ? 'true' : 'false' },
      { signal: controller.signal }
    )
      .then(response => {
        if (!controller.signal.aborted) {
          const data = response.data;
          const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
          setOffenders(list);
        }
      })
      .catch(err => {
        if (!controller.signal.aborted) {
          setError(err.message);
          setOffenders([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingOffenders(false);
      });

    return () => controller.abort();
  }, [userCoords, searchRadius, hideIncarcerated]);

  // ==========================================
  // Derived values (no effects needed)
  // ==========================================
  const mapCenter = useMemo(() => {
    if (userCoords) return [userCoords.lat, userCoords.lon];
    return DEFAULT_CENTER;
  }, [userCoords]);

  const mapZoom = userCoords ? LOCAL_ZOOM : DEFAULT_ZOOM;

  const heatmapPoints = useMemo(() => {
    return offenders
      .filter(o => o.latitude && o.longitude)
      .map(o => {
        const riskLabel = getRiskLabel(o.risk_level, o.tier_level);
        const intensity = riskLabel === 'High' ? 1.0 : riskLabel === 'Medium' ? 0.6 : 0.3;
        return [parseFloat(o.latitude), parseFloat(o.longitude), intensity];
      });
  }, [offenders]);

  const stats = useMemo(() => {
    const mappable = offenders.filter(o => o.latitude && o.longitude);
    const high = mappable.filter(o => getRiskLabel(o.risk_level, o.tier_level) === 'High').length;
    const medium = mappable.filter(o => getRiskLabel(o.risk_level, o.tier_level) === 'Medium').length;
    const low = mappable.filter(o => getRiskLabel(o.risk_level, o.tier_level) === 'Low').length;
    const unknown = mappable.length - high - medium - low;
    return { total: offenders.length, mappable: mappable.length, high, medium, low, unknown };
  }, [offenders]);

  const codeExample = `curl -H "X-API-Key: YOUR_KEY" \\
  "https://communityguardapi.com/api/data/offenders/nearby/\\
?latitude=38.90&longitude=-77.04&radius=5"

# Returns structured JSON:
# [{ "full_name": "...", "risk_level": "High",
#    "latitude": 38.91, "distance_miles": 0.8, ... }]`;

  const responseExample = `{
  "results": [
    {
      "id": 48291,
      "full_name": "John A Smith",
      "first_name": "John",
      "last_name": "Smith",
      "age": 45,
      "date_of_birth": "1981-03-15",
      "sex": "Male",
      "race": "White",
      "risk_level": "High",
      "designation": "Sexual Predator",
      "address_line1": "123 Main St",
      "city": "Arlington",
      "state_id": "VA",
      "zip_code": "22201",
      "latitude": 38.8816,
      "longitude": -77.0910,
      "distance_miles": 0.8,
      "offender_image_url": "https://...",
      "conviction_count": 2,
      "has_child_victim": true
    }
  ],
  "pagination": {
    "total_count": 847,
    "page": 1,
    "page_size": 50
  }
}`;

  const handleCopyCode = useCallback(() => {
    navigator.clipboard.writeText(codeExample).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [codeExample]);

  const radiusMeters = searchRadius * 1609.34;

  // ==========================================
  // Render
  // ==========================================
  return (
    <div className="min-h-[calc(100vh-128px)] flex flex-col items-center text-white">
      <Helmet>
        <title>CommunityGuardAPI — Sex Offender Registry API for Developers</title>
        <link rel="canonical" href="https://communityguardapi.com/" />
        <meta name="description" content="Access structured sex offender registry data from all 50 states via REST API. Search by name, location, or coordinates. 100 free calls/month." />
        <meta property="og:title" content="CommunityGuardAPI — Sex Offender Registry API for Developers" />
        <meta property="og:description" content="Access structured sex offender registry data from all 50 states via REST API. Search by name, location, or coordinates. 100 free calls/month." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://communityguardapi.com/" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      {/* Hero — compact on mobile */}
      <div className="w-full bg-gradient-to-b from-slate-900 to-slate-800 py-3 sm:py-6 px-4 text-center">
        <p className="text-xs sm:text-sm text-slate-400">
          {userZipCode ? `Showing registered offenders near ${userZipCode}` : 'Locate registered offenders in your area'}
        </p>
      </div>

      {/* Status Messages */}
      <div className="w-full max-w-7xl px-4 mt-4">
        {geoStatus === 'loading' && (
          <div className="pr-status-banner pr-status-warning">Requesting location permission...</div>
        )}
        {geoStatus === 'denied' && (
          <div className="pr-status-banner pr-status-error">
            Location permission denied. Please enable location services in your browser settings,
            or <Link to="/search" className="underline text-emerald-400 hover:text-emerald-300">search by name or address</Link> instead.
          </div>
        )}
        {geoStatus === 'error' && (
          <div className="pr-status-banner pr-status-error">Error getting your location.</div>
        )}
        {error && (
          <div className="pr-status-banner pr-status-error">Error loading offenders: {error}</div>
        )}
      </div>

      {/* Map Controls */}
      {userCoords && (
        <div className="w-full max-w-7xl px-4 mt-4">
          <div className="bg-slate-800/80 backdrop-blur-sm border border-slate-700 rounded-xl p-3 sm:p-4 space-y-3">
            {/* Radius slider — full width, prominent */}
            <div className="flex items-center gap-3">
              <label htmlFor="radius" className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold shrink-0">Radius</label>
              <input
                id="radius"
                type="range"
                min={1}
                max={25}
                step={1}
                value={searchRadius}
                onChange={(e) => setSearchRadius(Number(e.target.value))}
                aria-valuemin={1}
                aria-valuemax={25}
                aria-valuenow={searchRadius}
                aria-valuetext={`${searchRadius} miles`}
                className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer accent-emerald-500 bg-slate-600/80"
              />
              <span className="text-sm font-bold text-emerald-400 tabular-nums w-12 text-right shrink-0">{searchRadius} mi</span>
            </div>

            {/* Controls row */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* View toggle */}
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold shrink-0">View</span>
              {['map', 'gallery'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  aria-pressed={viewMode === mode}
                  className={`flex-1 py-1.5 sm:py-2 rounded-lg text-[11px] sm:text-xs font-semibold transition-all ${
                    viewMode === mode
                      ? 'bg-emerald-500/90 text-white'
                      : 'bg-slate-700/60 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                  }`}
                >
                  {mode === 'map' ? 'Map' : 'Gallery'}
                </button>
              ))}

              <div className="w-px h-5 bg-slate-700 shrink-0" />

              {/* Hide incarcerated — proper toggle switch */}
              <label className="flex items-center gap-2 cursor-pointer shrink-0">
                <span className="text-[10px] sm:text-xs text-slate-400">Hide Incarcerated</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={hideIncarcerated}
                  onClick={() => setHideIncarcerated(!hideIncarcerated)}
                  className={`relative w-9 h-5 rounded-full transition-colors ${hideIncarcerated ? 'bg-emerald-500' : 'bg-slate-600'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${hideIncarcerated ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </label>

              {/* Map-only controls: Heatmap / Satellite */}
              {viewMode === 'map' && (
                <>
                  <div className="w-px h-5 bg-slate-700 shrink-0" />
                  {[
                    { active: showHeatmap, toggle: () => setShowHeatmap(!showHeatmap), label: 'Heatmap' },
                    { active: satelliteView, toggle: () => setSatelliteView(!satelliteView), label: satelliteView ? 'Street' : 'Satellite' },
                  ].map((btn, i) => (
                    <button
                      key={i}
                      onClick={btn.toggle}
                      aria-pressed={btn.active}
                      className={`flex-1 py-1.5 sm:py-2 rounded-lg text-[11px] sm:text-xs font-semibold transition-all ${
                        btn.active
                          ? 'bg-emerald-500/90 text-white'
                          : 'bg-slate-700/60 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                      }`}
                    >
                      {btn.label}
                    </button>
                  ))}
                </>
              )}
            </div>

            {/* Legend — compact inline */}
            <div className="flex items-center justify-center gap-3 sm:gap-4">
              {[
                { color: 'bg-red-500', label: 'High' },
                { color: 'bg-orange-500', label: 'Med' },
                { color: 'bg-green-500', label: 'Low' },
                { color: 'bg-blue-500', label: 'Unknown' },
              ].map(({ color, label }) => (
                <span key={label} className="flex items-center gap-1 text-[10px] sm:text-xs text-slate-500">
                  <span className={`w-2 h-2 rounded-full ${color} inline-block`}></span>
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Gallery View */}
      {viewMode === 'gallery' && !loadingOffenders && offenders.length > 0 && (
        <div className="w-full max-w-7xl px-4 mt-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-1.5 sm:gap-2">
            {offenders.map(offender => {
              const riskLabel = getRiskLabel(offender.risk_level, offender.tier_level);
              const dist = userCoords && offender.latitude && offender.longitude
                ? calculateDistanceMiles(userCoords.lat, userCoords.lon, parseFloat(offender.latitude), parseFloat(offender.longitude))
                : null;
              const riskColor = riskLabel === 'High' ? 'bg-red-500' : riskLabel === 'Medium' ? 'bg-orange-500' : riskLabel === 'Low' ? 'bg-green-500' : 'bg-blue-500';
              const truncatedName = `${offender.first_name} ${offender.last_name ? offender.last_name.charAt(0) + '.' : ''}`;

              return (
                <Link
                  key={offender.id}
                  to={`/offenders/${offender.id}`}
                  className="bg-slate-800 rounded-lg overflow-hidden border border-slate-700/50 hover:border-emerald-500/50 transition-all"
                >
                  {/* Square photo area */}
                  <div className="relative aspect-square">
                    {offender.offender_image_url ? (
                      <img
                        src={offender.offender_image_url}
                        alt={`${offender.first_name} ${offender.last_name}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`w-full h-full ${offender.offender_image_url ? 'hidden' : 'flex'} items-center justify-center bg-slate-700`}>
                      <span className="text-2xl text-slate-500">?</span>
                    </div>
                  </div>
                  {/* Always-visible info strip */}
                  <div className="px-1.5 py-1.5 flex items-center gap-1 bg-slate-800">
                    <span className="text-[10px] sm:text-xs font-semibold text-white truncate flex-1">{truncatedName}</span>
                    <span className={`text-[8px] sm:text-[9px] px-1.5 py-0.5 rounded-full text-white shrink-0 ${riskColor}`}>
                      {riskLabel}
                    </span>
                    {dist !== null && (
                      <span className="text-[8px] sm:text-[9px] text-emerald-400 shrink-0">{dist.toFixed(1)}mi</span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Map */}
      {viewMode === 'map' && (
      <div className="w-full max-w-7xl px-4 mt-3">
        <div className="w-full h-[45vh] sm:h-[500px] md:h-[650px] rounded-xl overflow-hidden shadow-xl border border-slate-700" aria-label="Map showing registered offenders near your location">
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            scrollWheelZoom={true}
            style={{ height: '100%', width: '100%' }}
          >
            <ChangeView center={mapCenter} zoom={mapZoom} />
            <TileLayer
              key={satelliteView ? 'sat' : 'street'}
              attribution={satelliteView
                ? '&copy; Esri, Maxar, Earthstar Geographics'
                : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'}
              url={satelliteView
                ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
                : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'}
            />

            {/* User Location Marker */}
            {userCoords && (
              <Marker position={[userCoords.lat, userCoords.lon]} icon={userLocationIcon}>
                <Popup>
                  <div className="text-center">
                    <strong className="text-slate-800">Your Location</strong>
                    {userZipCode && <p className="text-xs text-slate-600 mt-1">ZIP: {userZipCode}</p>}
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Radius Circle */}
            {userCoords && (
              <Circle
                center={[userCoords.lat, userCoords.lon]}
                radius={radiusMeters}
                pathOptions={{
                  color: '#2ECC71',
                  fillColor: '#2ECC71',
                  fillOpacity: 0.04,
                  weight: 1.5,
                  dashArray: '6, 6',
                }}
              />
            )}

            {/* Heatmap Layer */}
            {showHeatmap && heatmapPoints.length > 0 && (
              <HeatmapLayer points={heatmapPoints} />
            )}

            {/* Offender Markers with Clustering */}
            {!loadingOffenders && !showHeatmap && (
              <MarkerClusterGroup
                chunkedLoading
                maxClusterRadius={50}
                spiderfyOnMaxZoom={true}
                showCoverageOnHover={false}
                iconCreateFunction={(cluster) => {
                  const count = cluster.getChildCount();
                  let sizeClass = 'pr-cluster-small';
                  if (count > 50) sizeClass = 'pr-cluster-large';
                  else if (count > 10) sizeClass = 'pr-cluster-medium';
                  return L.divIcon({
                    html: `<div><span>${count}</span></div>`,
                    className: `pr-marker-cluster ${sizeClass}`,
                    iconSize: L.point(40, 40),
                  });
                }}
              >
                {offenders
                  .filter(o => o.latitude && o.longitude)
                  .map(offender => {
                    const riskLabel = getRiskLabel(offender.risk_level, offender.tier_level);
                    const dist = userCoords
                      ? calculateDistanceMiles(userCoords.lat, userCoords.lon, parseFloat(offender.latitude), parseFloat(offender.longitude))
                      : null;

                    return (
                      <Marker
                        key={`offender-${offender.id}`}
                        position={[parseFloat(offender.latitude), parseFloat(offender.longitude)]}
                        icon={getMarkerIcon(offender.risk_level, offender.tier_level)}
                      >
                        <Popup minWidth={220} maxWidth={280}>
                          <div className="pr-popup">
                            {offender.offender_image_url && (
                              <img
                                src={offender.offender_image_url}
                                alt={offender.full_name || 'Offender'}
                                className="pr-popup-img"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <strong className="pr-popup-name">
                              {offender.first_name} {offender.last_name}
                            </strong>
                            <span className={getRiskBadgeClass(riskLabel)}>{riskLabel} Risk</span>
                            <p className="pr-popup-address">{formatPopupAddress(offender)}</p>
                            {dist !== null && (
                              <p className="pr-popup-distance">{dist.toFixed(1)} mi away</p>
                            )}
                            <Link to={`/offenders/${offender.id}`} className="pr-popup-link">
                              View Details
                            </Link>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
              </MarkerClusterGroup>
            )}
          </MapContainer>
        </div>
      </div>
      )}

      {/* Stats Bar */}
      {!loadingOffenders && offenders.length > 0 && (
        <div className="w-full max-w-7xl px-4 mt-3">
          <div className="bg-slate-800/80 backdrop-blur-sm border border-slate-700 rounded-xl p-2.5 sm:p-3">
            <div className="flex items-center justify-between gap-1">
              {[
                { value: stats.total, label: 'Total', color: 'text-white' },
                { value: stats.high, label: 'High', color: 'text-red-400' },
                { value: stats.medium, label: 'Med', color: 'text-orange-400' },
                { value: stats.low, label: 'Low', color: 'text-green-400' },
                { value: stats.unknown, label: 'Unknown', color: 'text-blue-400' },
              ].map(({ value, label, color }) => (
                <div key={label} className="flex-1 text-center">
                  <div className={`text-lg sm:text-2xl font-bold tabular-nums ${color}`}>{value}</div>
                  <div className="text-[9px] sm:text-[11px] text-slate-500 font-medium uppercase tracking-wide">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Loading indicator */}
      {loadingOffenders && (
        <div className="w-full max-w-7xl px-4 mt-4" role="status" aria-live="polite">
          <div className="bg-slate-800 rounded-lg p-6 text-center border border-slate-700">
            <div className="pr-spinner mx-auto mb-3"></div>
            <p className="text-slate-400">Loading offenders near {userZipCode || 'your location'}...</p>
          </div>
        </div>
      )}

      {/* No results */}
      {userCoords && !loadingOffenders && offenders.length === 0 && !error && (
        <div className="w-full max-w-7xl px-4 mt-4">
          <div className="bg-slate-800 rounded-lg p-6 text-center border border-slate-700">
            <p className="text-slate-400">No offenders found within {searchRadius} miles of your location.</p>
          </div>
        </div>
      )}

      {/* Attribution */}
      {geoStatus === 'granted' && (
        <p className="text-xs text-slate-500 mt-4 px-4">
          Data sourced from national and state-level sex offender registries. Refreshed monthly.
        </p>
      )}

      {/* ═══════════════════════════════════════════════════
          BELOW THE MAP: Sell the API
          ═══════════════════════════════════════════════════ */}

      {/* Divider */}
      <div className="w-full max-w-7xl px-4 mt-10 mb-8">
        <div className="border-t border-slate-700" />
      </div>

      {/* Value Prop: What you just saw = our API */}
      <div className="w-full max-w-4xl px-4 text-center mb-12">
        <h2 className="text-xl sm:text-3xl font-bold text-white mb-3 font-heading">
          Everything above? That's our <span className="text-emerald-400">API</span>.
        </h2>
        <p className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto mb-8">
          The map you just used is powered by the same REST API available to you.
          Structured, nationwide sex offender registry data — ready to integrate into your platform.
        </p>

        {/* Feature cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {[
            { icon: '50', label: 'States covered', sub: 'All U.S. jurisdictions' },
            { icon: '580K+', label: 'Records', sub: 'Updated monthly' },
            { icon: 'REST', label: 'JSON API', sub: 'Modern & documented' },
            { icon: '$0', label: 'To start', sub: '100 free calls/mo' },
          ].map(({ icon, label, sub }) => (
            <div key={label} className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
              <div className="text-xl sm:text-2xl font-bold text-emerald-400 mb-1">{icon}</div>
              <div className="text-xs sm:text-sm font-semibold text-white">{label}</div>
              <div className="text-[10px] sm:text-xs text-slate-500">{sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Code Example */}
      <div className="w-full max-w-3xl px-4 mb-12">
        <div className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800/50 border-b border-slate-700">
            <span className="text-xs font-mono text-slate-500">Quick Start</span>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCopyCode}
                className="text-[10px] sm:text-xs font-semibold text-slate-400 hover:text-white transition px-2 py-0.5 rounded border border-slate-600 hover:border-slate-500"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/60"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-green-500/60"></span>
              </div>
            </div>
          </div>
          <pre className="p-4 sm:p-5 text-xs sm:text-sm overflow-x-auto">
            <code className="text-slate-300">
{codeExample}
            </code>
          </pre>
        </div>
      </div>

      {/* API Response Example */}
      <div className="w-full max-w-3xl px-4 mb-12">
        <div className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800/50 border-b border-slate-700">
            <span className="text-xs font-mono text-slate-500">JSON Response</span>
            <span className="text-[10px] text-emerald-400 font-semibold">200 OK</span>
          </div>
          <pre className="p-4 sm:p-5 text-xs sm:text-sm overflow-x-auto max-h-80 overflow-y-auto">
            <code className="text-slate-300">
{responseExample}
            </code>
          </pre>
        </div>
      </div>

      {/* Who it's for */}
      <div className="w-full max-w-4xl px-4 mb-12">
        <h3 className="text-lg sm:text-xl font-bold text-white text-center mb-6 font-heading">Built for</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { title: 'Developers', desc: 'Integrate registry data into apps, platforms, and internal tools with a simple REST API.', cta: 'Read the Docs', to: '/api-docs' },
            { title: 'Schools & HOAs', desc: 'Automated screening for visitor management, community alerts, and campus safety systems.', cta: 'Learn More', to: '/about' },
            { title: 'Civic Platforms', desc: 'Power neighborhood safety tools, public records portals, and community notification systems.', cta: 'View Pricing', to: '/pricing' },
          ].map(({ title, desc, cta, to }) => (
            <div key={title} className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5 flex flex-col">
              <h4 className="text-sm font-bold text-white mb-2">{title}</h4>
              <p className="text-xs text-slate-400 flex-1 mb-3">{desc}</p>
              <Link to={to} className="text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition">
                {cta} &rarr;
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing teaser */}
      <div className="w-full max-w-3xl px-4 mb-12">
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-base sm:text-lg font-bold text-white mb-1 font-heading">Simple pay-as-you-go pricing</h3>
              <p className="text-xs sm:text-sm text-slate-400">
                100 free calls/month. Then <span className="text-white font-semibold">$0.08–$0.12</span> per call. No contracts.
              </p>
            </div>
            <Link
              to="/pricing"
              className="shrink-0 text-center px-5 py-2.5 border border-emerald-500 text-emerald-400 hover:bg-emerald-500/10 rounded-lg text-sm font-semibold transition"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="w-full max-w-3xl px-4 mb-12">
        <div className="bg-gradient-to-r from-emerald-500/10 to-slate-800/50 border border-emerald-500/30 rounded-xl p-6 sm:p-8 text-center">
          <h2 className="text-lg sm:text-2xl font-bold text-white mb-2 font-heading">Start building in minutes</h2>
          <p className="text-xs sm:text-sm text-slate-400 mb-5">
            Create a free account, get your API key, and start querying nationwide registry data.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/signup"
              className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg shadow-lg transition text-sm"
            >
              Get Your API Key — Free
            </Link>
            <Link
              to="/api-docs"
              className="px-6 py-2.5 border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 rounded-lg font-medium transition text-sm"
            >
              Read the Docs
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
